"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalOverrideAdapter = void 0;
const client_core_1 = require("@statsig/client-core");
const LOCAL_OVERRIDE_REASON = 'LocalOverride:Recognized';
function _makeEmptyStore() {
    return {
        gate: {},
        dynamicConfig: {},
        experiment: {},
        layer: {},
        paramStore: {},
    };
}
function _mergeOverrides(currentValues, newValues) {
    return {
        gate: Object.assign({}, currentValues.gate, newValues.gate),
        dynamicConfig: Object.assign({}, currentValues.dynamicConfig, newValues.dynamicConfig),
        experiment: Object.assign({}, currentValues.experiment, newValues.experiment),
        layer: Object.assign({}, currentValues.layer, newValues.layer),
        paramStore: Object.assign({}, currentValues.paramStore, newValues.paramStore),
    };
}
class LocalOverrideAdapter {
    constructor(sdkKey) {
        this._overrides = _makeEmptyStore();
        this._sdkKey = sdkKey !== null && sdkKey !== void 0 ? sdkKey : null;
    }
    _getLocalOverridesStorageKey(sdkKey) {
        return `statsig.local-overrides.${(0, client_core_1._getStorageKey)(sdkKey)}`;
    }
    loadFromStorage() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._sdkKey == null) {
                return;
            }
            if (!client_core_1.Storage.isReady()) {
                yield client_core_1.Storage.isReadyResolver();
            }
            const storageKey = this._getLocalOverridesStorageKey(this._sdkKey);
            const storedOverrides = client_core_1.Storage.getItem(storageKey);
            const overrides = storedOverrides
                ? (0, client_core_1._typedJsonParse)(storedOverrides, 'gate', 'LocalOverrideAdapter overrides')
                : null;
            const hasInMemoryOverrides = this._hasInMemoryOverrides();
            if (overrides) {
                // Merge overrides in memory with stored overrides.
                // WARN: This is a union of overrides. Trying to remove an override
                // before storage is ready won't remove the override in storage.
                this._overrides = hasInMemoryOverrides
                    ? _mergeOverrides(overrides, this._overrides)
                    : overrides;
            }
            if (hasInMemoryOverrides) {
                this._saveOverridesToStorage();
            }
        });
    }
    _saveOverridesToStorage() {
        if (this._sdkKey == null || !client_core_1.Storage.isReady()) {
            return;
        }
        const storageKey = this._getLocalOverridesStorageKey(this._sdkKey);
        client_core_1.Storage.setItem(storageKey, JSON.stringify(this._overrides));
    }
    overrideGate(name, value) {
        this._overrides.gate[name] = value;
        this._overrides.gate[(0, client_core_1._DJB2)(name)] = value;
        this._saveOverridesToStorage();
    }
    _warnIfStorageNotReady() {
        if (!client_core_1.Storage.isReady()) {
            client_core_1.Log.warn('Storage is not ready. Override removal may not persist.');
        }
    }
    removeGateOverride(name) {
        this._warnIfStorageNotReady();
        delete this._overrides.gate[name];
        delete this._overrides.gate[(0, client_core_1._DJB2)(name)];
        this._saveOverridesToStorage();
    }
    getGateOverride(current, _user) {
        var _a;
        const overridden = (_a = this._overrides.gate[current.name]) !== null && _a !== void 0 ? _a : this._overrides.gate[(0, client_core_1._DJB2)(current.name)];
        if (overridden == null) {
            return null;
        }
        return Object.assign(Object.assign({}, current), { value: overridden, details: Object.assign(Object.assign({}, current.details), { reason: LOCAL_OVERRIDE_REASON }) });
    }
    overrideDynamicConfig(name, value) {
        this._overrides.dynamicConfig[name] = value;
        this._overrides.dynamicConfig[(0, client_core_1._DJB2)(name)] = value;
        this._saveOverridesToStorage();
    }
    removeDynamicConfigOverride(name) {
        this._warnIfStorageNotReady();
        delete this._overrides.dynamicConfig[name];
        delete this._overrides.dynamicConfig[(0, client_core_1._DJB2)(name)];
        this._saveOverridesToStorage();
    }
    getDynamicConfigOverride(current, _user) {
        return this._getConfigOverride(current, this._overrides.dynamicConfig);
    }
    overrideExperiment(name, value) {
        this._overrides.experiment[name] = value;
        this._overrides.experiment[(0, client_core_1._DJB2)(name)] = value;
        this._saveOverridesToStorage();
    }
    removeExperimentOverride(name) {
        this._warnIfStorageNotReady();
        delete this._overrides.experiment[name];
        delete this._overrides.experiment[(0, client_core_1._DJB2)(name)];
        this._saveOverridesToStorage();
    }
    getExperimentOverride(current, _user) {
        return this._getConfigOverride(current, this._overrides.experiment);
    }
    overrideLayer(name, value) {
        this._overrides.layer[name] = value;
        this._overrides.layer[(0, client_core_1._DJB2)(name)] = value;
        this._saveOverridesToStorage();
    }
    removeLayerOverride(name) {
        this._warnIfStorageNotReady();
        delete this._overrides.layer[name];
        delete this._overrides.layer[(0, client_core_1._DJB2)(name)];
        this._saveOverridesToStorage();
    }
    overrideParamStore(name, value) {
        this._overrides.paramStore[name] = value;
        this._overrides.paramStore[(0, client_core_1._DJB2)(name)] = value;
        this._saveOverridesToStorage();
    }
    removeParamStoreOverride(name) {
        this._warnIfStorageNotReady();
        delete this._overrides.paramStore[name];
        delete this._overrides.paramStore[(0, client_core_1._DJB2)(name)];
        this._saveOverridesToStorage();
    }
    getParamStoreOverride(current) {
        var _a;
        const overridden = (_a = this._overrides.paramStore[current.name]) !== null && _a !== void 0 ? _a : this._overrides.paramStore[(0, client_core_1._DJB2)(current.name)];
        if (overridden == null) {
            return null;
        }
        const config = Object.assign({}, (current.__configuration || {}));
        for (const [key, value] of Object.entries(overridden)) {
            const paramType = this._inferParamType(value);
            config[key] = {
                ref_type: 'static',
                param_type: paramType,
                value,
            };
        }
        return {
            config,
            details: Object.assign(Object.assign({}, current.details), { reason: LOCAL_OVERRIDE_REASON }),
        };
    }
    getAllOverrides() {
        var _a;
        return ((_a = (0, client_core_1._typedJsonParse)(JSON.stringify(this._overrides), 'gate', 'OverrideStore')) !== null && _a !== void 0 ? _a : _makeEmptyStore());
    }
    removeAllOverrides() {
        this._warnIfStorageNotReady();
        this._overrides = _makeEmptyStore();
        this._saveOverridesToStorage();
    }
    getLayerOverride(current, _user) {
        var _a;
        const overridden = (_a = this._overrides.layer[current.name]) !== null && _a !== void 0 ? _a : this._overrides.layer[(0, client_core_1._DJB2)(current.name)];
        if (overridden == null) {
            return null;
        }
        return Object.assign(Object.assign({}, current), { __value: overridden, get: (0, client_core_1._makeTypedGet)(current.name, overridden), details: Object.assign(Object.assign({}, current.details), { reason: LOCAL_OVERRIDE_REASON }) });
    }
    _getConfigOverride(current, lookup) {
        var _a;
        const overridden = (_a = lookup[current.name]) !== null && _a !== void 0 ? _a : lookup[(0, client_core_1._DJB2)(current.name)];
        if (overridden == null) {
            return null;
        }
        return Object.assign(Object.assign({}, current), { value: overridden, get: (0, client_core_1._makeTypedGet)(current.name, overridden), details: Object.assign(Object.assign({}, current.details), { reason: LOCAL_OVERRIDE_REASON }) });
    }
    _hasInMemoryOverrides() {
        return (Object.keys(this._overrides.gate).length > 0 ||
            Object.keys(this._overrides.dynamicConfig).length > 0 ||
            Object.keys(this._overrides.experiment).length > 0 ||
            Object.keys(this._overrides.layer).length > 0 ||
            Object.keys(this._overrides.paramStore).length > 0);
    }
    _inferParamType(value) {
        if (typeof value === 'string')
            return 'string';
        if (typeof value === 'boolean')
            return 'boolean';
        if (typeof value === 'number')
            return 'number';
        if (Array.isArray(value))
            return 'array';
        return 'object';
    }
}
exports.LocalOverrideAdapter = LocalOverrideAdapter;
